import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "../../lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

/**
 * How long the pointer may be outside both the trigger and the tooltip before
 * the tooltip closes. Long enough to cross the gap onto the tooltip (which
 * stays hoverable, WCAG 1.4.13), short enough to read as closing on exit.
 */
const POINTER_EXIT_GRACE_MS = 150;

type PointerExit = { leave: () => void; enter: () => void };

const TooltipPointerExitContext = React.createContext<PointerExit | null>(null);

/**
 * Radix closes a hover tooltip only when a `pointermove` lands outside the
 * grace area between trigger and content, and it starts listening for that
 * move after a render. A pointer that leaves in one step — towards the global
 * header, say — or leaves the window produces no move it sees, so the tooltip
 * stayed open until Escape (GAP-40). Leaving the trigger or the tooltip now
 * also starts a short timer that entering either cancels.
 */
function Tooltip({
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    defaultOpen ?? false,
  );
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : uncontrolledOpen;
  const exitTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!controlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [controlled, onOpenChange],
  );
  const cancelExit = React.useCallback(() => {
    if (exitTimer.current == null) return;
    clearTimeout(exitTimer.current);
    exitTimer.current = null;
  }, []);
  const scheduleExit = React.useCallback(() => {
    cancelExit();
    exitTimer.current = setTimeout(() => {
      exitTimer.current = null;
      setOpen(false);
    }, POINTER_EXIT_GRACE_MS);
  }, [cancelExit, setOpen]);
  React.useEffect(() => cancelExit, [cancelExit]);

  const pointerExit = React.useMemo(
    () => ({ leave: scheduleExit, enter: cancelExit }),
    [scheduleExit, cancelExit],
  );

  return (
    <TooltipPointerExitContext.Provider value={pointerExit}>
      <TooltipPrimitive.Root open={open} onOpenChange={setOpen} {...props} />
    </TooltipPointerExitContext.Provider>
  );
}

/** Touch has no hover to leave; a tapped tooltip keeps Radix's behaviour. */
function isHoverPointer(event: React.PointerEvent): boolean {
  return event.pointerType !== "touch";
}

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ onPointerEnter, onPointerLeave, ...props }, ref) => {
  const pointerExit = React.useContext(TooltipPointerExitContext);
  return (
    <TooltipPrimitive.Trigger
      ref={ref}
      onPointerEnter={(event) => {
        onPointerEnter?.(event);
        pointerExit?.enter();
      }}
      onPointerLeave={(event) => {
        onPointerLeave?.(event);
        if (isHoverPointer(event)) pointerExit?.leave();
      }}
      {...props}
    />
  );
});
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(
  (
    { className, sideOffset = 4, onPointerEnter, onPointerLeave, ...props },
    ref,
  ) => {
    const pointerExit = React.useContext(TooltipPointerExitContext);
    return (
      <TooltipPrimitive.Portal>
        {/* Above Sheet overlay/content (z-[15000]), the same layer as
            select.tsx, so a tooltip opened inside a drawer is never hidden. */}
        <TooltipPrimitive.Content
          ref={ref}
          sideOffset={sideOffset}
          className={cn(
            "z-[16000] overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            className,
          )}
          onPointerEnter={(event) => {
            onPointerEnter?.(event);
            pointerExit?.enter();
          }}
          onPointerLeave={(event) => {
            onPointerLeave?.(event);
            if (isHoverPointer(event)) pointerExit?.leave();
          }}
          {...props}
        />
      </TooltipPrimitive.Portal>
    );
  },
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/**
 * The one place a disabled control gets a keyboard-reachable tooltip. A
 * disabled <button> takes no focus, so its "why" tooltip was mouse-only
 * (WCAG 2.1.1). Wrap it: `<TooltipTrigger asChild><DisabledTooltipAnchor>
 * <Button disabled …/></DisabledTooltipAnchor></TooltipTrigger>`. The anchor is
 * focusable while `focusable` is true; Radix wires the tooltip to it as its
 * description, and the wrapped control still announces its own name and
 * disabled state.
 */
const DisabledTooltipAnchor = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement> & { focusable?: boolean }
>(({ className, focusable = true, ...props }, ref) => (
  <span
    ref={ref}
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- the focus stop exists so keyboard users reach the tooltip of the disabled control inside; see the doc comment.
    tabIndex={focusable ? 0 : undefined}
    className={cn("inline-flex", className)}
    {...props}
  />
));
DisabledTooltipAnchor.displayName = "DisabledTooltipAnchor";

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  DisabledTooltipAnchor,
};
